import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"
import { setButtonLoading } from "../lib/button_loading"
import { syncGateReason } from "../lib/gate_reason"

// BUTTON-4: why Send Verification Email rests after a 429.
const RETRY_WAIT_REASON = "Too many tries. Wait a moment, then try again."

// Profile "Update Email Address" modal (#14065). The form still POSTs to
// request_email_change, but through request.js so a refusal — the address is
// another account's login (422) or the verification mail is throttled (429) —
// renders INLINE under the field with the server's exact sentence, instead of
// a flash after a full round trip. Success navigates to the server's
// redirect_to, where the flash notice shows as before.
//
// A refusal about the re-authentication password (#14176) names it in
// `data.field`, so that input — not the address — is marked invalid.
//
// Only the server writes copy here: every message is `data.error` verbatim.
// The one client string is the network-failure fallback. A 429 carries
// retry_after_seconds, and the submit stays disabled for exactly that long.
// Server strings go in via textContent — never innerHTML.
const NETWORK_FAILURE = "Something went wrong. Please try again."

export default class extends Controller {
  static targets = ["form", "emailInput", "passwordInput", "submitBtn", "message"]

  disconnect() {
    this.clearRetryTimer()
  }

  async submit(event) {
    event.preventDefault()
    if (this.submitBtnTarget.disabled) return

    this.clearMessage()
    this.setBusy(true)
    try {
      const response = await post(this.formTarget.action, {
        body: new FormData(this.formTarget),
        responseKind: "json"
      })
      const data = await response.json
      if (response.ok && data.redirect_to) {
        this.navigate(data.redirect_to)
        return // stay disabled: the page is leaving
      }
      this.showError(data.error || NETWORK_FAILURE, data.field)
      this.holdSubmit(data.retry_after_seconds)
    } catch {
      this.showError(NETWORK_FAILURE)
      this.setBusy(false)
    }
  }

  // Separate so a spec can observe it — jsdom's window.location.assign cannot
  // be replaced.
  navigate(url) {
    window.location.assign(url)
  }

  // A 429 holds the button for the server's cooldown; anything else releases
  // it at once so the agent can correct the address.
  holdSubmit(retryAfterSeconds) {
    this.clearRetryTimer()
    const seconds = Number(retryAfterSeconds)
    if (!Number.isFinite(seconds) || seconds <= 0) {
      this.setBusy(false)
      return
    }
    // The request is done, so the button is no longer busy: it rests,
    // disabled with its reason, until the server's cooldown ends.
    this.setBusy(false)
    syncGateReason(this.submitBtnTarget, { ready: false, reason: RETRY_WAIT_REASON })
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null
      syncGateReason(this.submitBtnTarget, { ready: true })
    }, seconds * 1000)
  }

  showError(text, field) {
    this.messageTarget.classList.remove("d-none")
    this.messageTarget.replaceChildren(document.createTextNode(text))
    this.invalidInput(field).setAttribute("aria-invalid", "true")
  }

  clearMessage() {
    this.messageTarget.classList.add("d-none")
    this.messageTarget.replaceChildren()
    this.emailInputTarget.removeAttribute("aria-invalid")
    if (this.hasPasswordInputTarget) this.passwordInputTarget.removeAttribute("aria-invalid")
  }

  invalidInput(field) {
    if (field === "current_password" && this.hasPasswordInputTarget) return this.passwordInputTarget
    return this.emailInputTarget
  }

  // BUTTON-8: the shared busy state — spinner in the icon's place, aria-busy,
  // label unchanged, second click blocked.
  setBusy(busy) {
    setButtonLoading(this.submitBtnTarget, busy)
  }

  clearRetryTimer() {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer)
      this.retryTimer = null
    }
  }
}
